// Phoenix-channel client. One lazily-connected module-level Socket
// is reused across page transitions. Auth is a signed Phoenix.Token
// from /api/me — token (not cookie) avoids a CSRF round-trip per connect.

import { Socket, Channel } from "phoenix";
import * as api from "./client";
import type { DeckRef } from "./client";

let socket: Socket | null = null;
let cachedToken: string | null = null;
let presenceChannel: Channel | null = null;
let userStateChannel: Channel | null = null;

async function ensureSocketToken(): Promise<string> {
  if (cachedToken) return cachedToken;
  const me = await api.getMe();
  if (!me.socket_token) {
    throw new Error("server did not return a socket token");
  }
  cachedToken = me.socket_token;
  return me.socket_token;
}

/** Seed cache from a login response; null/undefined clears. The
 *  optional shape matches `MeResponse.socket_token`, which is absent
 *  for banned users. */
export function setSocketToken(token: string | null | undefined): void {
  cachedToken = token ?? null;
  if (!token) {
    if (presenceChannel) {
      presenceChannel.leave();
      presenceChannel = null;
    }
    if (userStateChannel) {
      userStateChannel.leave();
      userStateChannel = null;
    }
    if (socket) {
      socket.disconnect();
      socket = null;
    }
    return;
  }

  // Eagerly open the socket on login so `presence:lobby` tracks the
  // user even if they never visit a page that opens a channel itself
  // (admin, deck builder, world). Failures here are non-fatal — the
  // next page-driven getSocket() call will retry.
  void getSocket().catch(() => {});
}

export async function getSocket(): Promise<Socket> {
  const token = await ensureSocketToken();
  if (socket && socket.isConnected()) return socket;
  if (!socket) {
    socket = new Socket("/socket", { params: { token }, logger: undefined });
    // After a transport-level reconnect we may have missed patches
    // while disconnected. A single /api/me refetch reconciles the
    // cache; the user-state channel will resume streaming patches on
    // the next message. Fire-and-forget — failures fall back to the
    // next route change's RequireAuth probe.
    socket.onOpen(() => {
      void api.refreshMe().catch(() => {});
    });
  }
  if (!socket.isConnected()) socket.connect();
  ensurePresenceChannel(socket);
  ensureUserStateChannel(socket);
  return socket;
}

// Joined once per socket. The channel only calls `Presence.track` so
// the server can count distinct online users; nothing pushes back.
function ensurePresenceChannel(sock: Socket): void {
  if (presenceChannel) return;
  const channel = sock.channel("presence:lobby", {});
  channel.join().receive("error", () => {
    // Swallow — runtime stats degrades gracefully if the join fails.
    presenceChannel = null;
  });
  presenceChannel = channel;
}

// Joined once per socket. The server pushes `"patch"` events
// whenever any per-user state (HP, currencies, sleep, unlocked
// factions, deck-slot cap) changes — including changes driven by
// another tab/device or by server-internal writes (engine HP
// persistence, admin actions). The payload is shallow-merged into
// the `useMe` cache so the AppHeader chips repaint in the same
// frame without an /api/me roundtrip.
function ensureUserStateChannel(sock: Socket): void {
  if (userStateChannel) return;
  const me = api.getCachedMe();
  if (!me) return;
  const channel = sock.channel(`user:${me.id}`, {});
  channel.on("patch", (payload: Record<string, unknown>) => {
    api.applyMePatch(payload);
  });
  channel.join().receive("error", () => {
    // Swallow — live sync degrades gracefully (page navigations
    // still refresh /api/me). Reset so the next connect retries.
    userStateChannel = null;
  });
  userStateChannel = channel;
}

export interface RoomPlayerView {
  user_id: number;
  username: string;
  /** True for users created via `loginAsGuest()` — surfaced here so
   *  the room UI can tag those seats with a "(guest)" indicator. */
  is_guest: boolean;
  /** "player" for the two seat-holders, "spectator" for everyone else. */
  role: "player" | "spectator";
  deck: DeckSelection | null;
  ready: boolean;
  /** True while their post-game banner is still up. */
  still_in_match: boolean;
}

/** Shared decks arrive without `entries` — the server keeps the full
 *  decklist for match start, but the room UI only needs name/faction
 *  and the deck ref to select by. */
export interface SharedDeckView {
  deck: DeckRef;
  name: string;
  faction: string;
  shared_by: number;
  shared_by_username: string;
}

export type DeckSelection =
  | { kind: "owned"; deck: DeckRef }
  | { kind: "shared"; shared_by: number; deck: DeckRef };

export interface RoomSnapshot {
  id: string;
  host_user_id: number;
  state: "waiting" | "counting_down" | "starting" | "in_game" | "post_game";
  /** The two player seats (length 0, 1, or 2). */
  players: RoomPlayerView[];
  /** Everyone else in the room. Unbounded; can also be empty. */
  spectators: RoomPlayerView[];
  shared_decks: SharedDeckView[];
  countdown_remaining_ms: number | null;
  engine_session_id: string | null;
  game_ids: Record<number, number>;
}

export interface MatchStartedPayload {
  engine_session_id: string;
  game_ids: Record<number, number>;
}

export interface MatchEndedPayload {
  progressions: Record<number, unknown>;
}

export interface RoomChannelHandlers {
  onState: (snap: RoomSnapshot) => void;
  onMatchStarted: (p: MatchStartedPayload) => void;
  onMatchEnded: (p: MatchEndedPayload) => void;
  onError: (msg: string) => void;
}

export interface RoomConnection {
  channel: Channel;
  selectDeck: (sel: DeckSelection | null) => void;
  toggleReady: () => Promise<void>;
  shareDeck: (deck: {
    deck: DeckRef;
    name: string;
    faction: string;
    entries: Array<[string, number]>;
  }) => void;
  /** Host-only: move `targetUserId` between player seat / spectator
   *  pool. Server rejects from non-hosts, during the match, or when
   *  promoting would exceed two players. */
  setRole: (
    targetUserId: number,
    role: "player" | "spectator",
  ) => Promise<void>;
  leave: () => void;
  close: () => void;
}

export async function joinRoom(
  roomId: string,
  handlers: RoomChannelHandlers,
): Promise<{ initial: RoomSnapshot; you: number; conn: RoomConnection }> {
  const sock = await getSocket();
  const channel = sock.channel(`room:${roomId}`, {});

  channel.on("state", (msg: RoomSnapshot) => handlers.onState(msg));
  channel.on("match_started", (msg: MatchStartedPayload) =>
    handlers.onMatchStarted(msg),
  );
  channel.on("match_ended", (msg: MatchEndedPayload) =>
    handlers.onMatchEnded(msg),
  );
  channel.on("error", (msg: { reason?: string }) =>
    handlers.onError(msg.reason ?? "unknown error"),
  );

  return new Promise((resolve, reject) => {
    channel
      .join()
      .receive("ok", (resp: { state: RoomSnapshot; you: number }) => {
        const conn: RoomConnection = {
          channel,
          selectDeck: (sel) => {
            const payload = normalizeSelection(sel);
            channel.push("select_deck", payload);
          },
          toggleReady: () =>
            new Promise<void>((res, rej) => {
              channel
                .push("toggle_ready", {})
                .receive("ok", () => res())
                .receive("error", (e: { reason?: string }) =>
                  rej(new Error(e.reason ?? "toggle_ready failed")),
                );
            }),
          shareDeck: (deck) => {
            channel.push("share_deck", deck);
          },
          setRole: (targetUserId, role) =>
            new Promise<void>((res, rej) => {
              channel
                .push("set_role", { user_id: targetUserId, role })
                .receive("ok", () => res())
                .receive("error", (e: { reason?: string }) =>
                  rej(new Error(e.reason ?? "set_role failed")),
                );
            }),
          leave: () => {
            channel.push("leave", {});
            channel.leave();
          },
          // Drop the channel without telling the server "I left", so
          // RoomPage's unmount on navigate-to-battle doesn't evict us.
          close: () => {
            channel.leave();
          },
        };
        resolve({ initial: resp.state, you: resp.you, conn });
      })
      .receive("error", (resp: { reason?: string }) => {
        reject(new Error(resp.reason ?? "join failed"));
      });
  });
}

// ---- per-game push channel ----

import type { ActionBody, ActionResponse, WireEvent } from "./types";
import type { PreviewState } from "./client";

export type GameEndedOutcome = "win" | "loss" | null;

/** Seat-level verdict — present on every `game_ended` push so
 *  spectators (who have no per-user `outcome`) can render the
 *  GameOverBanner without re-fetching the deleted engine session. */
export type GameEndedResult =
  | { kind: "winner"; player: 0 | 1 }
  | { kind: "draw" }
  | null;

export interface GameChannelHandlers {
  /** v2 push: opponent's per-seat wire events. `first_seq` is the seq
   *  of `events[0]` — when `events` is empty (no-op action for the
   *  recipient), `first_seq` is `-1`. Consumers gap-check
   *  `first_seq === lastEventSeq + 1` and resync via `GET /state` on
   *  mismatch. */
  onEvents: (payload: { events: WireEvent[]; first_seq: number }) => void;
  /** `outcome` is non-null on forfeit / claim-victory paths (the
   *  recipient never saw the engine declare a result). Null on
   *  natural end-of-match. `result` is the seat-level verdict,
   *  needed by spectators (who have no `outcome`). Matches no
   *  longer carry an XP/currency progression payload. */
  onGameEnded: (payload: {
    outcome: GameEndedOutcome;
    result: GameEndedResult;
    /** Set only on a no-winner abandonment (`"idle_evicted"`,
     *  `"superseded"`, `"unrecoverable"`) so the recipient can show a
     *  specific reason instead of dropping silently. */
    reason: string | null;
  }) => void;
}

/** Error from a game-channel `"action"` / `"state"` request. `reason`
 *  carries the server's machine-readable code — `"not_found"` is the
 *  channel equivalent of the HTTP 404 (match ended / session gone),
 *  `"rate_limited"`, `"spectators_read_only"` and `"engine_error"`
 *  mirror the HTTP route's error JSON; `"timeout"` is client-side. */
export class GameChannelError extends Error {
  reason: string;

  constructor(reason: string) {
    super(reason);
    this.name = "GameChannelError";
    this.reason = reason;
  }
}

/** The state reply carries the same wrapper fields the HTTP
 *  `GET /api/games/:id/state` response does (personas, `seat`,
 *  `room_id`, …) — reuse the client's response type so BattlePage
 *  consumes both sources interchangeably. */
export type GameStateResponse = Awaited<
  ReturnType<typeof api.getGameStateV2>
>;

export interface GameChannelHandle {
  leave: () => void;
  /** Submit an engine action over the channel — the in-battle
   *  replacement for `POST /api/games/:id/action`. Resolves with the
   *  same `ActionResponse` body; rejects with `GameChannelError`. */
  submitAction: (
    body: ActionBody,
  ) => Promise<ActionResponse & { stale?: boolean }>;
  /** Fetch the v2 `MatchSnapshot` over the channel — the in-battle
   *  replacement for `GET /api/games/:id/state`. Accepts the same
   *  preview / `viewer_seat` options; rejects with `GameChannelError`. */
  fetchState: (preview?: PreviewState) => Promise<GameStateResponse>;
}

/** Exported for tests. */
export function pushForReply<T>(
  channel: Channel,
  event: string,
  payload: object,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    channel
      .push(event, payload)
      .receive("ok", (resp: T) => resolve(resp))
      .receive("error", (resp: { reason?: string }) =>
        reject(new GameChannelError(resp?.reason ?? "engine_error")),
      )
      .receive("timeout", () => reject(new GameChannelError("timeout")));
  });
}

/** Per-engine-session battle channel — joined by every BattlePage
 *  (PvP and single-player). Carries the server's `events` /
 *  `game_ended` pushes and the SPA's `action` / `state` requests.
 *  `viewerSeat` is only meaningful for spectators — it's surfaced in
 *  presence so the server can later route per-perspective pushes
 *  (not yet wired). */
export async function joinGameChannel(
  engineSessionId: string,
  handlers: GameChannelHandlers,
  opts?: { viewerSeat?: 0 | 1 },
): Promise<GameChannelHandle & { spectating: boolean }> {
  const sock = await getSocket();
  const params: Record<string, unknown> = {};
  if (opts?.viewerSeat === 0 || opts?.viewerSeat === 1) {
    params.viewer_seat = opts.viewerSeat;
  }
  const channel = sock.channel(`game:${engineSessionId}`, params);

  channel.on(
    "events",
    (msg: { events: WireEvent[]; first_seq: number }) =>
      handlers.onEvents({ events: msg.events ?? [], first_seq: msg.first_seq ?? -1 }),
  );
  channel.on(
    "game_ended",
    (msg: {
      outcome?: string | null;
      result?: { kind?: string; player?: number } | null;
      reason?: string | null;
    }) => {
      const o =
        msg.outcome === "win" || msg.outcome === "loss" ? msg.outcome : null;
      let result: GameEndedResult = null;
      if (msg.result) {
        if (
          msg.result.kind === "winner" &&
          (msg.result.player === 0 || msg.result.player === 1)
        ) {
          result = { kind: "winner", player: msg.result.player };
        } else if (msg.result.kind === "draw") {
          result = { kind: "draw" };
        }
      }
      handlers.onGameEnded({ outcome: o, result, reason: msg.reason ?? null });
    },
  );

  return new Promise((resolve, reject) => {
    channel
      .join()
      .receive("ok", (resp: { spectating?: boolean } = {}) => {
        resolve({
          leave: () => channel.leave(),
          spectating: resp.spectating === true,
          submitAction: (body) =>
            pushForReply<ActionResponse & { stale?: boolean }>(
              channel,
              "action",
              body,
            ),
          fetchState: (preview) =>
            pushForReply<GameStateResponse>(
              channel,
              "state",
              stateRequestBody(preview),
            ),
        });
      })
      .receive("error", (resp: { reason?: string }) => {
        reject(new Error(resp.reason ?? "game channel join failed"));
      })
      // Without this the promise never settles when the socket is
      // down, and BattlePage's HTTP fallback never fires. Leave the
      // channel so phoenix stops rejoining a join we've abandoned.
      .receive("timeout", () => {
        channel.leave();
        reject(new GameChannelError("timeout"));
      });
  });
}

/** Channel `"state"` bodies send real JSON arrays — unlike the HTTP
 *  query path, which comma-joins them (`previewToQuery`). Omit empty
 *  keys so the common no-preview fetch stays a tiny frame.
 *  Exported for tests. */
export function stateRequestBody(preview?: PreviewState): object {
  const body: Record<string, unknown> = {};
  if (preview?.preview_attackers?.length) {
    body.preview_attackers = preview.preview_attackers;
  }
  if (preview?.preview_blocks?.length) {
    body.preview_blocks = preview.preview_blocks;
  }
  if (preview?.viewer_seat === 0 || preview?.viewer_seat === 1) {
    body.viewer_seat = preview.viewer_seat;
  }
  return body;
}

function normalizeSelection(sel: DeckSelection | null): object {
  // Phoenix.Channel.push needs an object — encode null as {kind:"none"}.
  if (sel == null) return { kind: "none" };
  if (sel.kind === "owned") return { kind: "owned", deck: sel.deck };
  return {
    kind: "shared",
    shared_by: sel.shared_by,
    deck: sel.deck,
  };
}
